import logo from "assets/images/logo/Trellis_Logomark_color.png";
import TermsOfService from "components/custom/instructions/TermsOfService";
import { Component, type RefObject } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Redirect, Route, Switch, type RouteComponentProps } from "react-router-dom";
import store, { type RootState } from "redux/store";
import routes, { type RouteDefinition } from "routes/routes";
import Footer from "../views/Footer";
import NavBar from "../views/NavigationBar";
import Sidebar from "../views/Sidebar";
import { setChannel } from "redux/reducers/channel";
import checkOnboardRedirect from "components/custom/onboarding/checkOnboardRedirect";
import { createRef } from "react";
import BeamerInAppNotifications from "components/custom/beamer";
import { selectApplications } from "redux/selectors/applications";
import ChromeAccessToken from "components/custom/ChromeAccessToken";
import SignupLayout from "views/onboarding/layout/SignupLayout";
import Banner from "components/custom/Banner";
import SpApiTokenExpirationBanner from "components/custom/SpApiTokenExpirationBanner";
import type { StaticContext } from "react-router";

type Props = PropsFromRedux & RouteComponentProps<Record<never, string>, StaticContext, { from?: string } | undefined>;

interface State {
  mwsOrSummary: string;
}

class Main extends Component<Props, State> {
  mainPanel: RefObject<HTMLDivElement>;

  constructor(props: Props) {
    super(props);
    this.state = {
      mwsOrSummary: "summary",
    };

    this.mainPanel = createRef();
  }

  componentDidMount() {
    if (!this.props.isAuthenticated) {
      this.props.history.push("/");
    }

    if (document.documentElement.className.includes("nav-open")) {
      document.documentElement.classList.toggle("nav-open");
    }

    // check if we need to redirect to onboarding
    const redirect = checkOnboardRedirect(this.props.amz_onboard);

    // The only redirect used in onboarding v2 is for the SP-API screen from legacy onboarding, which is used to process
    // the SP-API tokens from a registered URL, then redirects back to the new flow
    const { bypass_onboarding } = this.props.user;
    const {
      amazon_advertising_connecting,
      amazon_spapi_access,
      eu_amazon_spapi_access,
      fe_amazon_spapi_access,
      amazon_advertising_access,
    } = this.props.amz_onboard;

    if (redirect !== null && !bypass_onboarding) {
      this.props.history.push(redirect);
    }

    if (
      bypass_onboarding &&
      redirect === "/onboard/amzspapi" &&
      [amazon_spapi_access, eu_amazon_spapi_access, fe_amazon_spapi_access].includes("in_progress")
    ) {
      this.props.history.push(redirect);
      return;
    }

    // In new sign up flow, don't go back to amzadv route, instead return to Amazon API auth screen
    if (
      (bypass_onboarding &&
        redirect === "/onboard/amzadv" &&
        this.props.amz_onboard.amazon_advertising_access !== "pending") ||
      (bypass_onboarding && amazon_advertising_connecting === true && amazon_advertising_access !== "connected")
    ) {
      this.props.history.push("/onboarding/v2/connect");
      // Redirect to amazon summary page if reauth or additional advertising accounts being provided
    } else if (
      this.props.history.location.state?.from !== "onboardingSummary" &&
      this.props.amz_onboard.amazon_advertising_access === "connected"
    ) {
      if (localStorage.secondaryChannel === "amazon" && localStorage.channel === "walmart") {
        store.dispatch(this.props.setChannel("amazon"));
        localStorage.channel = "amazon";
      }
    }

    document.body.classList.add("sidebar-mini");
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.props.isAuthenticated) {
      this.props.history.push("/");
    }

    const { bypass_onboarding, customer_type, total_monthly_sales } = this.props.user;

    // Below handles cases where users sign up and create a login method, but havn't gone through the rest of the onboarding flow
    if (bypass_onboarding && (!customer_type || (customer_type !== "other" && !total_monthly_sales))) {
      this.props.history.push("/onboarding/v2/about/type");
    }

    const onboardRedirectTo = localStorage.getItem("onboardRedirectTo");
    // Redirect to dashboard/initial adplan view when categories are generated through metadata API
    if (onboardRedirectTo && onboardRedirectTo.includes("/dashboard")) {
      localStorage.removeItem("onboardRedirectTo");
      this.props.history.push("/user/dashboard");
    }

    if (
      !(
        this.props.authLoading ||
        this.props.marketPlace.marketPlace === "None" ||
        (this.props.amz_onboard.listings_progress !== "complete" &&
          this.props.amz_onboard.listings_progress !== "not applicable") ||
        (this.props.amz_onboard.category_progress !== "complete" &&
          this.props.amz_onboard.category_progress !== "not applicable") ||
        (this.props.amz_onboard.stock_progress !== "complete" &&
          this.props.amz_onboard.stock_progress !== "not applicable")
      )
    ) {
      if (prevProps.history.action === "PUSH") {
        document.documentElement.scrollTop = 0;
        if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
        if (this.mainPanel.current) {
          this.mainPanel.current.scrollTop = 0;
        }
      }
      if (
        window.innerWidth < 993 &&
        prevProps.history.action === "PUSH" &&
        document.documentElement.className.includes("nav-open")
      ) {
        document.documentElement.classList.toggle("nav-open");
      }
    }
  }

  getRoutes = (routes: RouteDefinition[]) => {
    return routes
      .filter((r) => !(r.application && !this.props.applications.find((a) => a === r.application)))
      .map((r, index) => {
        if (r.layout === "/user") {
          return (
            <Route
              path={r.layout + r.path}
              key={index}
              exact={r.exact}
              render={r.component ? (routeProps) => <r.component {...routeProps} layout={r.layout} /> : undefined}
            >
              {r.children?.map((child) => (
                <Route
                  key={child.path}
                  path={child.path}
                  exact={child.exact}
                  render={(routeProps) => <child.component {...routeProps} layout={r.layout} />}
                />
              ))}
            </Route>
          );
        } else {
          return null;
        }
      });
  };

  redirectUser = () => {
    if (
      this.props.applications.find((a) => a === "Search Analytics") &&
      !this.props.applications.find((a) => a === "Advertising")
    ) {
      return "/user/searchanalytics";
    } else if (this.props.user.permissions === "reports") {
      return "/user/report/advertisement";
    }

    return "/user/dashboard";
  };

  render() {
    const { bypass_onboarding } = this.props.user;
    const { amazon_spapi_access, eu_amazon_spapi_access, fe_amazon_spapi_access } = this.props.amz_onboard;

    if (
      bypass_onboarding &&
      [amazon_spapi_access, eu_amazon_spapi_access, fe_amazon_spapi_access].includes("in_progress")
    ) {
      return <SignupLayout placeholderLayout={true} />;
    }

    return (
      <>
        {this.props.authLoading || this.props.marketPlace.marketPlace === "None" ?
          <div className="wrap" style={{ height: "100vh" }}>
            <img src={logo} alt="Loading..." className="rotate" width="80" height="auto" />
          </div>
        : <div className="wrapper">
            {/* functions */}
            <BeamerInAppNotifications />
            {/* SIDEBAR */}
            <Sidebar />
            <div className="main-panel" ref={this.mainPanel}>
              {/* NAVBAR */}
              <NavBar {...this.props} />
              <Banner />
              <SpApiTokenExpirationBanner />
              <ChromeAccessToken />

              {/* CONTENT PANEL */}
              <Switch>
                {this.getRoutes(routes)}
                <Redirect to={this.redirectUser()} />
              </Switch>

              {/* FOOTER */}
              <Footer fluid />
            </div>
            {this.props.user.agreementStatus === "not_agreed" ?
              <TermsOfService />
            : null}
          </div>
        }
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  isAuthenticated: state.auth.isAuthenticated,
  authLoading: state.auth.loading,
  amz_onboard: state.amz_onboard,
  marketPlace: state.marketPlace,
  user: state.user,
  channel: state.channel,
  applications: selectApplications(state),
});

const mapDispatchToProps = {
  setChannel,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Main);
